import { useEffect, useMemo, useState, useSyncExternalStore, type FormEvent } from 'react';
import type {
  QuestionBankCategory,
  QuestionBankKnowledgePoint,
  QuestionBankProblem,
  QuestionBankSection,
  QuestionRequirementsRichText,
} from '@wx-weiqi/contracts';

import type { QuestionBankStore } from '../stores/question-bank-store';
import { BoardSurface } from './BoardSurface';
import { QuestionBankDirectory } from './QuestionBankDirectory';
import { QuestionRequirementsEditor } from './QuestionRequirementsEditor';

export const QUESTION_BANK_SECTION_LABELS: Readonly<Record<QuestionBankSection, string>> = {
  LIFE_AND_DEATH: '死活',
  CAPTURING_RACE: '对杀',
  TESUJI: '手筋',
  CAPTURE: '吃子',
  OPENING: '布局',
  MIDDLE_GAME: '中盘',
  ENDGAME: '官子',
  JOSEKI: '定式',
};

const SECTIONS = Object.keys(QUESTION_BANK_SECTION_LABELS) as QuestionBankSection[];

export function buildQuestionBankTree(categories: readonly QuestionBankCategory[]) {
  return SECTIONS.map((section) => ({
    section,
    label: QUESTION_BANK_SECTION_LABELS[section],
    categories: categories.filter((category) => category.section === section),
  }));
}

type Editor =
  | Readonly<{ kind: 'CREATE_CATEGORY' }>
  | Readonly<{ kind: 'EDIT_CATEGORY'; category: QuestionBankCategory }>
  | Readonly<{ kind: 'CREATE_POINT'; category: QuestionBankCategory }>
  | Readonly<{ kind: 'EDIT_POINT'; point: QuestionBankKnowledgePoint }>
  | Readonly<{ kind: 'EDIT_PROBLEM'; problem: QuestionBankProblem }>
  | Readonly<{ kind: 'IMPORT' }>;

function editorTitle(editor: Editor): string {
  switch (editor.kind) {
    case 'EDIT_PROBLEM':
      return '编辑题目要求';
    case 'CREATE_CATEGORY':
      return '新增分类';
    case 'EDIT_CATEGORY':
      return '修改分类名称';
    case 'CREATE_POINT':
      return '新增知识点';
    case 'EDIT_POINT':
      return '修改知识点名称';
    case 'IMPORT':
      return '题目导入';
  }
}

export function QuestionBankPanel({ store }: { readonly store: QuestionBankStore }) {
  const state = useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);
  const [view, setView] = useState<'BANK' | 'KNOWLEDGE'>('BANK');
  const [section, setSection] = useState<QuestionBankSection>('LIFE_AND_DEATH');
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<ReadonlySet<string>>(new Set());
  const [showPreviews, setShowPreviews] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [name, setName] = useState('');
  const [requirements, setRequirements] = useState('');
  const [requirementsRichText, setRequirementsRichText] =
    useState<QuestionRequirementsRichText | null>(null);
  const [requirementsValid, setRequirementsValid] = useState(true);
  const [archive, setArchive] = useState<File | null>(null);
  const busy = state.pending !== null;

  useEffect(() => {
    void store.loadCatalog().catch(() => undefined);
  }, [store]);

  const sectionCategories = useMemo(
    () => state.categories.filter((category) => category.section === section),
    [section, state.categories],
  );
  const selectedPoint = state.categories
    .flatMap((category) => category.knowledgePoints)
    .find((point) => point.knowledgePointId === state.selectedKnowledgePointId);
  const treeSections = useMemo(() => buildQuestionBankTree(state.categories), [state.categories]);

  const openEditor = (next: Editor) => {
    store.clearMessages();
    setName(
      next.kind === 'EDIT_CATEGORY'
        ? next.category.name
        : next.kind === 'EDIT_POINT'
          ? next.point.name
          : '',
    );
    setArchive(null);
    setRequirements(next.kind === 'EDIT_PROBLEM' ? (next.problem.requirements ?? '') : '');
    setRequirementsRichText(
      next.kind === 'EDIT_PROBLEM' ? (next.problem.requirementsRichText ?? null) : null,
    );
    setRequirementsValid(true);
    setEditor(next);
  };

  const toggleExpanded = (categoryId: string) => {
    setExpandedCategoryIds((current) => {
      const next = new Set(current);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const submitEditor = (event: FormEvent) => {
    event.preventDefault();
    if (editor === null || (editor.kind === 'EDIT_PROBLEM' && !requirementsValid)) return;
    const operation =
      editor.kind === 'EDIT_PROBLEM'
        ? store.updateProblemRequirements(editor.problem, requirements, requirementsRichText)
        : editor.kind === 'CREATE_CATEGORY'
          ? store.createCategory(section, name)
          : editor.kind === 'EDIT_CATEGORY'
            ? store.updateCategory(editor.category, name)
            : editor.kind === 'CREATE_POINT'
              ? store.createKnowledgePoint(editor.category.categoryId, name)
              : editor.kind === 'EDIT_POINT'
                ? store.updateKnowledgePoint(editor.point, name)
                : archive === null
                  ? Promise.reject(new TypeError('请选择 ZIP 文件。'))
                  : store.importProblems(archive);
    void operation.then(() => setEditor(null)).catch(() => undefined);
  };

  return (
    <div className="question-bank-manager">
      <nav className="question-bank-tabs" aria-label="题库管理视图">
        <button
          className={view === 'BANK' ? 'is-active' : ''}
          type="button"
          onClick={() => setView('BANK')}
        >
          题库
        </button>
        <button
          className={view === 'KNOWLEDGE' ? 'is-active' : ''}
          type="button"
          onClick={() => setView('KNOWLEDGE')}
        >
          知识分类
        </button>
      </nav>

      <div className="question-bank-messages" aria-live="polite">
        {state.error !== null ? <p className="message message--error">{state.error}</p> : null}
        {state.notice !== null ? <p className="message message--success">{state.notice}</p> : null}
      </div>

      {view === 'KNOWLEDGE' ? (
        <section className="question-knowledge-view">
          <div className="question-section-tabs" aria-label="围棋知识板块">
            {SECTIONS.map((candidate) => (
              <button
                key={candidate}
                type="button"
                className={section === candidate ? 'is-active' : ''}
                onClick={() => setSection(candidate)}
              >
                {QUESTION_BANK_SECTION_LABELS[candidate]}
              </button>
            ))}
          </div>
          <div className="question-category-list">
            {sectionCategories.map((category) => {
              const expanded = expandedCategoryIds.has(category.categoryId);
              return (
                <article className="question-category-row" key={category.categoryId}>
                  <div className="question-category-main">
                    <button
                      className="question-category-toggle"
                      type="button"
                      aria-expanded={expanded}
                      onClick={() => toggleExpanded(category.categoryId)}
                    >
                      <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
                      <strong>{category.name}</strong>
                      <small>{category.knowledgePoints.length} 个知识点</small>
                    </button>
                    <div className="question-row-actions">
                      <button
                        type="button"
                        onClick={() => openEditor({ kind: 'EDIT_CATEGORY', category })}
                      >
                        修改
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          if (window.confirm(`确认删除分类“${category.name}”？`)) {
                            void store.deleteCategory(category).catch(() => undefined);
                          }
                        }}
                      >
                        删除
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditor({ kind: 'CREATE_POINT', category })}
                      >
                        新增知识点
                      </button>
                    </div>
                  </div>
                  {expanded ? (
                    <div className="question-point-list">
                      {category.knowledgePoints.map((point) => (
                        <div className="question-point-row" key={point.knowledgePointId}>
                          <span>
                            <strong>{point.name}</strong>
                            <small>{point.problemCount} 道题</small>
                          </span>
                          <div className="question-row-actions">
                            <button
                              type="button"
                              onClick={() => openEditor({ kind: 'EDIT_POINT', point })}
                            >
                              修改
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                if (window.confirm(`确认删除知识点“${point.name}”？`)) {
                                  void store.deleteKnowledgePoint(point).catch(() => undefined);
                                }
                              }}
                            >
                              删除
                            </button>
                          </div>
                        </div>
                      ))}
                      {category.knowledgePoints.length === 0 ? (
                        <p className="question-inline-empty">尚未创建知识点目录。</p>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              );
            })}
            {sectionCategories.length === 0 && !busy ? (
              <div className="question-bank-empty">
                <strong>{QUESTION_BANK_SECTION_LABELS[section]}板块暂无分类</strong>
                <span>点击“新增分类”开始整理知识目录。</span>
              </div>
            ) : null}
          </div>
          <footer className="question-bank-footer">
            <button
              className="primary-button"
              type="button"
              onClick={() => openEditor({ kind: 'CREATE_CATEGORY' })}
            >
              新增分类
            </button>
          </footer>
        </section>
      ) : (
        <section className="question-library-view question-library-view--directory">
          <QuestionBankDirectory
            sections={treeSections}
            selectedPointId={state.selectedKnowledgePointId}
            loading={state.pending === 'load-catalog'}
            onSelect={(pointId) => void store.selectKnowledgePoint(pointId).catch(() => undefined)}
          />

          <div className="question-library-content">
            <div className={`question-grid${showPreviews ? ' question-grid--preview' : ''}`}>
              {state.problems.map((problem) => (
                <article className="question-card" key={problem.problemId}>
                  <div className="question-card-preview">
                    {showPreviews ? (
                      <BoardSurface
                        size={problem.boardSize}
                        stones={problem.stones}
                        initialAnnotations={problem.initialAnnotations ?? []}
                        lastMove={null}
                      />
                    ) : (
                      <span>未生成预览</span>
                    )}
                  </div>
                  <strong title={problem.filename}>{problem.filename}</strong>
                  <small>
                    {problem.boardSize} 路 · {problem.currentPlayer === 'BLACK' ? '黑先' : '白先'}
                  </small>
                  <div className="question-card-actions">
                    <button
                      type="button"
                      className="question-edit-button"
                      disabled={busy}
                      onClick={() => openEditor({ kind: 'EDIT_PROBLEM', problem })}
                    >
                      编辑
                    </button>
                    <button
                      className="question-delete-button"
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm(`确认删除题目“${problem.filename}”？`)) {
                          void store.deleteProblem(problem).catch(() => undefined);
                        }
                      }}
                    >
                      删除
                    </button>
                  </div>
                </article>
              ))}
              {state.problems.length === 0 && !busy ? (
                <div className="question-bank-empty question-bank-empty--grid">
                  <strong>{selectedPoint === undefined ? '尚未选择目录' : '该目录暂无题目'}</strong>
                  <span>
                    {selectedPoint === undefined
                      ? '请从左侧选择知识点。'
                      : '使用“题目导入”上传 ZIP。'}
                  </span>
                </div>
              ) : null}
            </div>
            <footer className="question-library-footer">
              <nav aria-label="题目分页">
                <button
                  type="button"
                  disabled={
                    busy || state.problemPage <= 1 || state.selectedKnowledgePointId === null
                  }
                  onClick={() =>
                    state.selectedKnowledgePointId === null
                      ? undefined
                      : void store
                          .selectKnowledgePoint(
                            state.selectedKnowledgePointId,
                            state.problemPage - 1,
                          )
                          .catch(() => undefined)
                  }
                >
                  上一页
                </button>
                <span>
                  第 {state.problemPage} / {state.problemPageCount} 页
                </span>
                <button
                  type="button"
                  disabled={
                    busy ||
                    state.problemPage >= state.problemPageCount ||
                    state.selectedKnowledgePointId === null
                  }
                  onClick={() =>
                    state.selectedKnowledgePointId === null
                      ? undefined
                      : void store
                          .selectKnowledgePoint(
                            state.selectedKnowledgePointId,
                            state.problemPage + 1,
                          )
                          .catch(() => undefined)
                  }
                >
                  下一页
                </button>
              </nav>
              <div>
                <button
                  className="secondary-button"
                  type="button"
                  disabled={
                    state.selectedKnowledgePointId === null || state.totalProblems < 2 || busy
                  }
                  title="当前目录全部题目：日期从早到晚，同一天按题号从小到大排序并保存"
                  onClick={() => void store.sortProblems().catch(() => undefined)}
                >
                  {state.pending === 'sort-problems' ? '排序中…' : '自动排序'}
                </button>
                <button
                  className="secondary-button"
                  type="button"
                  disabled={state.selectedKnowledgePointId === null}
                  onClick={() => setShowPreviews((current) => !current)}
                >
                  {showPreviews ? '收起预览图' : '生成预览图'}
                </button>
                <button
                  className="primary-button"
                  type="button"
                  disabled={state.selectedKnowledgePointId === null || busy}
                  onClick={() => openEditor({ kind: 'IMPORT' })}
                >
                  题目导入
                </button>
              </div>
            </footer>
          </div>
        </section>
      )}

      {editor !== null ? (
        <div
          className="question-editor-backdrop"
          onMouseDown={(event) => event.target === event.currentTarget && !busy && setEditor(null)}
        >
          <form
            className="question-editor-card"
            onSubmit={submitEditor}
            role="dialog"
            aria-modal="true"
            aria-label={editorTitle(editor)}
          >
            <header>
              <div>
                <p className="eyebrow">题库管理</p>
                <h3>{editorTitle(editor)}</h3>
              </div>
              <button
                type="button"
                aria-label="关闭"
                disabled={busy}
                onClick={() => setEditor(null)}
              >
                ×
              </button>
            </header>
            {editor.kind === 'IMPORT' ? (
              <label className="question-upload-field">
                ZIP 文件
                <input
                  type="file"
                  accept=".zip,application/zip"
                  required
                  onChange={(event) => setArchive(event.target.files?.[0] ?? null)}
                />
                <small>最多 20 MiB、200 个 SGF 文件；导入前会进行围棋规则校验。</small>
              </label>
            ) : editor.kind === 'EDIT_PROBLEM' ? (
              <div className="question-requirements-field">
                <span>题目要求</span>
                <small className="question-edit-filename">{editor.problem.filename}</small>
                <QuestionRequirementsEditor
                  key={editor.problem.problemId}
                  initialText={requirements}
                  initialRichText={requirementsRichText}
                  disabled={busy}
                  onChange={(text, richText, valid) => {
                    setRequirements(text);
                    setRequirementsRichText(richText);
                    setRequirementsValid(valid);
                  }}
                />
              </div>
            ) : (
              <label>
                名称
                <input
                  value={name}
                  required
                  minLength={1}
                  maxLength={64}
                  autoFocus
                  placeholder="请输入名称"
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
            )}
            <div className="question-editor-actions">
              <button
                className="secondary-button"
                type="button"
                disabled={busy}
                onClick={() => setEditor(null)}
              >
                取消
              </button>
              <button
                className="primary-button"
                type="submit"
                disabled={
                  busy ||
                  (editor.kind === 'EDIT_PROBLEM' && !requirementsValid) ||
                  (editor.kind !== 'IMPORT' &&
                    editor.kind !== 'EDIT_PROBLEM' &&
                    name.trim().length === 0)
                }
              >
                {busy ? '正在保存…' : editor.kind === 'IMPORT' ? '上传并导入' : '保存'}
              </button>
            </div>
            {state.error !== null ? (
              <p role="alert" className="message message--error">
                {state.error}
              </p>
            ) : null}
          </form>
        </div>
      ) : null}
    </div>
  );
}
